import {
  useState, useMemo 
} from 'react';
import {
  TrendingUp,
  Search,
  X,
  Target,
  Clock,
  CheckCircle,
  AlertTriangle,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CrudToolbar } from '@/components/ui/crud-toolbar';
import {
  useGoals,
  GoalCard,
  GoalDetailModal,
} from '@/features/performance';
import type {
  GoalStatus, GoalPriority, Goal 
} from '@/features/performance';
import { toast } from 'sonner';

const GOAL_STATUSES: {
  value: GoalStatus;
  label: string 
}[] = [
  {
    value: 'not-started',
    label: 'Not Started' 
  },
  {
    value: 'in-progress',
    label: 'In Progress' 
  },
  {
    value: 'at-risk',
    label: 'At Risk' 
  },
  {
    value: 'completed',
    label: 'Completed' 
  },
  {
    value: 'cancelled',
    label: 'Cancelled' 
  },
];

const GOAL_PRIORITIES: {
  value: GoalPriority;
  label: string 
}[] = [
  {
    value: 'low',
    label: 'Low' 
  },
  {
    value: 'medium',
    label: 'Medium' 
  },
  {
    value: 'high',
    label: 'High' 
  },
  {
    value: 'critical',
    label: 'Critical' 
  },
];

export function GoalsView() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<GoalStatus | ''>('');
  const [priorityFilter, setPriorityFilter] = useState<GoalPriority | ''>('');
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);

  const {
    data, isLoading, error, refetch 
  } = useGoals({
    filters: {
      search: searchQuery || undefined,
      status: statusFilter || undefined,
      priority: priorityFilter || undefined,
    },
  });

  const goalsList = data?.data ?? [];

  const stats = useMemo(() => {
    const inProgress = goalsList.filter((g) => g.status === 'in-progress').length;
    const atRisk = goalsList.filter((g) => g.status === 'at-risk').length;
    const completed = goalsList.filter((g) => g.status === 'completed').length;
    const avgProgress =
      goalsList.length > 0
        ? Math.round(goalsList.reduce((sum, g) => sum + g.progress, 0) / goalsList.length)
        : 0;
    return {
      inProgress,
      atRisk,
      completed,
      avgProgress,
      total: goalsList.length,
    };
  }, [goalsList]);

  const handleAddGoal = () => {
    toast.info('Create goal functionality coming soon');
  };

  const handleEditGoal = (goal: Goal) => {
    toast.info(`Edit goal: ${goal.title}`);
    setSelectedGoal(null);
  };

  const handleDeleteGoal = (goal: Goal) => {
    toast.success(`Deleted goal: ${goal.title}`);
    setSelectedGoal(null);
    refetch();
  };

  const handleUpdateProgress = (goal: Goal) => {
    toast.info(`Update progress for: ${goal.title}`);
    setSelectedGoal(null);
  };

  const handleMarkComplete = (goal: Goal) => {
    toast.success(`Goal completed: ${goal.title}`);
    setSelectedGoal(null);
    refetch();
  };

  const handleCardClick = (goal: Goal) => {
    setSelectedGoal(goal);
  };

  const exportData = goalsList.map((g) => ({
    employee: g.employeeName,
    role: g.employeeRole,
    title: g.title,
    description: g.description,
    category: g.category,
    priority: g.priority,
    status: g.status,
    progress: g.progress,
    targetDate: g.targetDate,
    assignedBy: g.assignedByName,
    site: g.siteName,
    completedAt: g.completedAt ?? '',
  }));

  return (
    <>
      <CrudToolbar
        entityName="Goal"
        entityNamePlural="Goals"
        selectedCount={0}
        onAdd={handleAddGoal}
        exportData={exportData}
        exportFilename="performance-goals.csv"
      />

      <GoalDetailModal
        goal={selectedGoal}
        isOpen={!!selectedGoal}
        onClose={() => setSelectedGoal(null)}
        onEdit={handleEditGoal}
        onDelete={handleDeleteGoal}
        onUpdateProgress={handleUpdateProgress}
        onMarkComplete={handleMarkComplete}
      />

      <StatsCards stats={stats} />

      <SearchAndFilter
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        priorityFilter={priorityFilter}
        setPriorityFilter={setPriorityFilter}
      />

      <GoalsGrid
        goalsList={goalsList}
        isLoading={isLoading}
        error={error}
        onCardClick={handleCardClick}
        onRefetch={refetch}
      />
    </>
  );
}

interface StatsCardsProps {
  readonly stats: {
    total: number;
    inProgress: number;
    atRisk: number;
    completed: number;
    avgProgress: number;
  };
}

function StatsCards({ stats }: StatsCardsProps) {
  return (
    <div className="grid gap-4 md:grid-cols-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Total Goals</CardTitle>
          <TrendingUp className="h-5 w-5 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.total}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">In Progress</CardTitle>
          <Clock className="h-5 w-5 text-blue-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-blue-600">{stats.inProgress}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">At Risk</CardTitle>
          <AlertTriangle className="h-5 w-5 text-red-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-red-600">{stats.atRisk}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Completed</CardTitle>
          <CheckCircle className="h-5 w-5 text-green-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
        </CardContent>
      </Card>
    </div>
  );
}

interface SearchAndFilterProps {
  readonly searchQuery: string;
  readonly setSearchQuery: (query: string) => void;
  readonly statusFilter: GoalStatus | '';
  readonly setStatusFilter: (status: GoalStatus | '') => void;
  readonly priorityFilter: GoalPriority | '';
  readonly setPriorityFilter: (priority: GoalPriority | '') => void;
}

function SearchAndFilter({
  searchQuery,
  setSearchQuery,
  statusFilter,
  setStatusFilter,
  priorityFilter,
  setPriorityFilter,
}: SearchAndFilterProps) {
  return (
    <div className="flex gap-4">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search goals..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full rounded-md border bg-background py-2 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <select
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value as GoalStatus | '')}
        className="rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
      >
        <option value="">All Statuses</option>
        {GOAL_STATUSES.map((s) => (
          <option key={s.value} value={s.value}>
            {s.label}
          </option>
        ))}
      </select>
      <select
        value={priorityFilter}
        onChange={(e) => setPriorityFilter(e.target.value as GoalPriority | '')}
        className="rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
      >
        <option value="">All Priorities</option>
        {GOAL_PRIORITIES.map((p) => (
          <option key={p.value} value={p.value}>
            {p.label}
          </option>
        ))}
      </select>
    </div>
  );
}

interface GoalsGridProps {
  readonly goalsList: Goal[];
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly onCardClick: (goal: Goal) => void;
  readonly onRefetch: () => void;
}

function GoalsGrid({
  goalsList,
  isLoading,
  error,
  onCardClick,
  onRefetch,
}: GoalsGridProps) {
  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={`skel-${i.toString()}`} className="h-64 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <TrendingUp className="mb-4 h-12 w-12 text-red-500" />
          <p className="text-lg font-medium">Failed to load goals</p>
          <Button variant="outline" className="mt-4" onClick={onRefetch}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (goalsList.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Target className="mb-4 h-12 w-12 text-muted-foreground" />
          <p className="text-lg font-medium">No goals found</p>
          <p className="text-muted-foreground">Create a goal to get started</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {goalsList.map((goal) => (
        <GoalCard key={goal.goalId} goal={goal} onClick={() => onCardClick(goal)} />
      ))}
    </div>
  );
}
